/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toET, parseDateOnly } from '../utils/et';
import { format } from 'date-fns';
import openFile from '../utils/openFile';

// B-060 (2026-09-10): document types come from the server (the PUC Driver
// Qualification File list with renewal intervals) so this page and the
// compliance matrix can never disagree about what is required.

const STATUS_STYLES = {
  pending:  { bg: '#FAEEDA', color: '#854F0B', label: 'Pending' },
  approved: { bg: '#E1F5EE', color: '#0F6E56', label: 'Approved' },
  rejected: { bg: '#FCEBEB', color: '#A32D2D', label: 'Rejected' },
};

// Matrix cell colors: worst states loudest.
const CELL = {
  missing:  { bg: '#FCEBEB', color: '#A32D2D', label: 'Missing' },
  expired:  { bg: '#F09595', color: '#5A0F0F', label: 'Expired' },
  rejected: { bg: '#FCEBEB', color: '#A32D2D', label: 'Rejected' },
  expiring: { bg: '#FAEEDA', color: '#854F0B', label: 'Expiring' },
  pending:  { bg: '#EDF4FB', color: '#185FA5', label: 'Review' },
  ok:       { bg: '#E1F5EE', color: '#0F6E56', label: 'OK' },
};

function Badge({ bg, color, label }) {
  return <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>;
}

// Short column headers for the matrix (full labels stay in the per-driver list).
function shortLabel(label) {
  return label
    .replace(' (signed)', '').replace(' (state, at hire)', '').replace(' (annual pull)', '')
    .replace(' (or equivalent)', '').replace(', prior 7 days (at hire)', '').replace("Medical Examiner's", 'Med')
    .replace('Previous Employer Safety History Request', 'Prior Employer')
    .replace('Drug & Alcohol Clearinghouse Query', 'Clearinghouse')
    .replace('Pre-Employment Drug & Alcohol Test', 'Pre-Emp D&A')
    .replace('Annual Review of Driving Record', 'Annual Review')
    .replace('Motor Vehicle Record', 'MVR').replace('Employment Application', 'Application')
    .replace('3-Year Driving Record', '3-yr Record').replace("Driver's License", 'License');
}

export default function PersonnelFiles() {
  const [drivers, setDrivers] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [files, setFiles] = useState([]);
  const [latestByType, setLatestByType] = useState({});
  const [expiring, setExpiring] = useState([]);
  const [companyFiles, setCompanyFiles] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [showMatrix, setShowMatrix] = useState(true);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadMatrix = () => api.get('/files/compliance').then(r => setMatrix(r.data)).catch(() => {});

  useEffect(() => {
    const load = async () => {
      try {
        const [dRes, eRes, cRes, tRes] = await Promise.all([
          api.get('/drivers'),
          api.get('/files/expiring?days=30'),
          api.get('/files/company'),
          api.get('/files/doc-types'),
        ]);
        setDrivers(dRes.data.drivers || []);
        setExpiring(eRes.data.expiring || []);
        setCompanyFiles(cRes.data.files || []);
        setDocTypes((tRes.data.docTypes || []).filter(t => t.key !== 'other'));
        if (dRes.data.drivers?.length > 0) setSelectedDriver(dRes.data.drivers[0].id);
        await loadMatrix();
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const refreshDriver = () => {
    if (!selectedDriver) return Promise.resolve();
    return api.get(`/files/driver/${selectedDriver}`).then(r => {
      setFiles(r.data.files || []);
      setLatestByType(r.data.latestByType || {});
    }).catch(() => {});
  };

  useEffect(() => { refreshDriver(); }, [selectedDriver]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdminUpload = async (docType, file) => {
    if (!file || !selectedDriver) return;
    const spec = docTypes.find(t => t.key === docType);
    // Ask for the date on the document so the renewal clock is right. Blank = today.
    let issuedAt = '';
    let expiresAt = '';
    if (spec && spec.renewMonths) {
      const typed = window.prompt(`Date on the ${spec.label} (YYYY-MM-DD). Leave blank for today. Expires ${spec.renewMonths} months after that date.`, '');
      if (typed === null) return;
      issuedAt = typed.trim();
    } else if (spec && spec.hasExpiry) {
      const typed = window.prompt(`Expiration date printed on the ${spec.label} (YYYY-MM-DD). Leave blank if none.`, '');
      if (typed === null) return;
      expiresAt = typed.trim();
    }
    setUploadingDoc(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'personnel');
      formData.append('docType', docType);
      formData.append('driverId', selectedDriver);
      if (issuedAt) formData.append('issuedAt', issuedAt);
      if (expiresAt) formData.append('expiresAt', expiresAt);
      // uploaderId intentionally omitted — null uploader_id = admin upload
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshDriver();
      await loadMatrix();
    } catch (err) {
      alert('Upload failed — ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleReview = async (fileId, status) => {
    try {
      await api.patch(`/files/${fileId}/review`, { status });
      await refreshDriver();
      await loadMatrix();
    } catch (err) { alert('Failed to update'); }
  };

  const handleUploadCompany = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingDoc('company');
    try {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('category', 'company');
      formData.append('docType', 'training_material');
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await api.get('/files/company');
      setCompanyFiles(r.data.files || []);
    } catch (err) { alert('Upload failed'); }
    finally { setUploadingDoc(null); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get('/files/compliance.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `driver-qualification-files-${matrix?.asOf || 'today'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Export failed'); }
    finally { setExporting(false); }
  };

  const requiredTypes = docTypes.filter(t => t.required);
  const optionalTypes = docTypes.filter(t => !t.required);
  const missingDocs = requiredTypes.filter(dt => !latestByType[dt.key]);

  const renderDocRow = (dt, i, count) => {
    const file = latestByType[dt.key];
    const status = file ? STATUS_STYLES[file.status] : null;
    const isExpired = file?.expires_at && new Date(file.expires_at) < new Date();
    return (
      <div key={dt.key} style={{ padding: '10px 14px', borderBottom: i < count - 1 ? '0.5px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {dt.label}
            {dt.renewMonths && <span style={{ fontSize: 10, color: '#999', fontWeight: 400 }}> · every {dt.renewMonths} mo</span>}
          </div>
          {file ? (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {file.original_filename} · {format(toET(file.created_at), 'MMM d, yyyy')}
              {file.expires_at && (
                <span style={{ color: isExpired ? '#A32D2D' : '#999' }}>
                  {' · '}{isExpired ? 'EXPIRED' : 'Exp'} {format(parseDateOnly(file.expires_at), 'MMM d, yyyy')}
                </span>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: dt.required ? '#A32D2D' : '#999', marginTop: 2 }}>{dt.required ? 'Not uploaded' : 'Optional — not uploaded'}</div>
          )}
        </div>
        {status && <Badge {...status} />}
        <div style={{ display: 'flex', gap: 4 }}>
          {file && (
            <a href="#" onClick={(e) => openFile(file.id, e)}
              style={{ fontSize: 10, padding: '3px 8px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4', borderRadius: 4, textDecoration: 'none' }}>View</a>
          )}
          {file?.status === 'pending' && (
            <>
              <button onClick={() => handleReview(file.id, 'approved')} style={{ fontSize: 10, padding: '3px 8px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Approve</button>
              <button onClick={() => handleReview(file.id, 'rejected')} style={{ fontSize: 10, padding: '3px 8px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>Reject</button>
            </>
          )}
          <label style={{ fontSize: 10, padding: '3px 8px', background: '#f0f0f0', border: '0.5px solid #e0e0db', borderRadius: 4, cursor: 'pointer' }}>
            {uploadingDoc === dt.key ? '...' : file ? 'Replace' : 'Upload'}
            <input type="file" accept="image/*,.pdf,.doc,.docx,.txt,.rtf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; handleAdminUpload(dt.key, f); }} style={{ display: 'none' }} />
          </label>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Personnel &amp; Company Files</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
            {matrix ? `${matrix.summary.fullyCompliant} of ${matrix.summary.drivers} drivers audit-ready · ${matrix.summary.totalGaps} gaps` : 'Driver qualification files'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setShowMatrix(s => !s)} style={{ fontSize: 11, padding: '5px 10px', background: '#f7f7f5', border: '0.5px solid #e0e0db' }}>
            {showMatrix ? 'Hide matrix' : 'Show matrix'}
          </button>
          <button onClick={exportCsv} disabled={exporting} style={{ fontSize: 11, padding: '5px 10px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #90BEE8' }}>
            {exporting ? 'Exporting...' : 'Export for PUC audit (CSV)'}
          </button>
        </div>
      </div>

      {/* Compliance matrix: every active driver x every required document */}
      {showMatrix && matrix && (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, marginBottom: 16, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 10px', position: 'sticky', left: 0, background: 'white', fontWeight: 500 }}>Driver</th>
                <th style={{ padding: '8px 6px', fontWeight: 500 }}>Gaps</th>
                {matrix.docTypes.map(t => (
                  <th key={t.key} title={t.label} style={{ padding: '8px 6px', fontWeight: 500, whiteSpace: 'nowrap', color: '#666' }}>{shortLabel(t.label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map(r => (
                <tr key={r.driverId} onClick={() => setSelectedDriver(r.driverId)} style={{ cursor: 'pointer', background: r.driverId === selectedDriver ? '#F7FAFF' : 'white', borderTop: '0.5px solid #f0f0f0' }}>
                  <td style={{ padding: '6px 10px', fontWeight: 500, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: r.driverId === selectedDriver ? '#F7FAFF' : 'white' }}>{r.name}</td>
                  <td style={{ padding: '6px', textAlign: 'center', color: r.gaps ? '#A32D2D' : '#0F6E56', fontWeight: 600 }}>{r.gaps}</td>
                  {matrix.docTypes.map(t => {
                    const c = r.cells[t.key]; const s = CELL[c.state];
                    return (
                      <td key={t.key} title={c.expires ? `${s.label} · exp ${c.expires}` : s.label} style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: 52, padding: '2px 4px', borderRadius: 4, background: s.bg, color: s.color, fontWeight: 500 }}>
                          {s.label}{c.expires && (c.state === 'expiring' || c.state === 'expired') ? <><br /><span style={{ fontSize: 9 }}>{c.expires.slice(5)}</span></> : ''}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expiring alerts */}
      {expiring.length > 0 && (
        <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#854F0B', marginBottom: 6 }}>Documents expiring within 30 days</div>
          {expiring.map(f => (
            <div key={f.id} style={{ fontSize: 11, color: '#854F0B', marginBottom: 2 }}>
              {f.driver_name} — {f.doc_type.replace(/_/g, ' ')} expires {format(parseDateOnly(f.expires_at), 'MMM d, yyyy')}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <select value={selectedDriver || ''} onChange={e => setSelectedDriver(e.target.value)} style={{ width: '100%', fontSize: 13, padding: '8px' }}>
          {drivers.map(d => (
            <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
          ))}
        </select>
      </div>

      {missingDocs.length > 0 && (
        <div style={{ fontSize: 12, color: '#A32D2D', marginBottom: 12 }}>
          Missing required: {missingDocs.map(d => d.label).join(', ')}
        </div>
      )}

      {loading ? <div style={{ fontSize: 12, color: '#999' }}>Loading...</div> : (
        <>
          <div style={{ fontSize: 11, color: '#999', margin: '0 0 4px 2px' }}>Required for the PUC Driver Qualification File</div>
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
            {requiredTypes.map((dt, i) => renderDocRow(dt, i, requiredTypes.length))}
          </div>
          {optionalTypes.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#999', margin: '0 0 4px 2px' }}>Optional</div>
              <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
                {optionalTypes.map((dt, i) => renderDocRow(dt, i, optionalTypes.length))}
              </div>
            </>
          )}
        </>
      )}

      {/* Company / training files */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0 6px' }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Company &amp; training files</div>
        <label style={{ fontSize: 10, padding: '3px 8px', background: '#f0f0f0', border: '0.5px solid #e0e0db', borderRadius: 4, cursor: 'pointer' }}>
          {uploadingDoc === 'company' ? '...' : 'Upload'}
          <input type="file" onChange={handleUploadCompany} style={{ display: 'none' }} />
        </label>
      </div>
      <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
        {companyFiles.length === 0 && <div style={{ padding: '10px 14px', fontSize: 12, color: '#999' }}>No company files uploaded.</div>}
        {companyFiles.map((f, i) => (
          <div key={f.id} style={{ padding: '10px 14px', borderBottom: i < companyFiles.length - 1 ? '0.5px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{f.original_filename}</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                {format(toET(f.created_at), 'MMM d, yyyy')} · acknowledged by {f.ack_count} of {f.total_drivers} drivers
              </div>
            </div>
            <a href="#" onClick={(e) => openFile(f.id, e)}
              style={{ fontSize: 10, padding: '3px 8px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4', borderRadius: 4, textDecoration: 'none' }}>View</a>
          </div>
        ))}
      </div>
    </div>
  );
}
